import { useContext, useEffect, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { InnerWrapper, MainContainer } from "../../../common";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useWalletContext } from "../../../../context/WalletContext";
import { EMIONBOARDINGSTATUS, VERIFIED } from "../../../../constants";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { HeadingText, ParagraphText, UBButton } from "../../../../Libraries";
import { WalletNonidealModal } from "../../../common/Modals";
import {
  BackIconNew,
  LogoRupinNew,
  SliderNextIcon,
} from "../../../../assets/icons/icon";
import slideQr from "../../../../assets/images/img-01.png";
import slideBanks from "../../../../assets/images/img-02.png";
import slideWallet from "../../../../assets/images/img-03.png";
import styles from "./WalletOnboardingB.module.css";

const SLIDES = [
  {
    titleKey: "wallet_onboarding_b_slide3_title",
    subtitleKey: "wallet_onboarding_b_slide3_subtitle",
    altKey: "wallet_onboarding_b_slide3_alt",
    image: slideQr,
    imageClass: styles.slideImageQr,
  },
  {
    titleKey: "wallet_onboarding_b_slide2_title",
    subtitleKey: "wallet_onboarding_b_slide2_subtitle",
    altKey: "wallet_onboarding_b_slide2_alt",
    image: slideBanks,
    imageClass: styles.slideImageBanks,
  },
  {
    titleKey: "wallet_onboarding_b_slide1_title",
    subtitleKey: "wallet_onboarding_b_slide1_subtitle",
    altKey: "wallet_onboarding_b_slide1_alt",
    image: slideWallet,
    imageClass: styles.slideImageWallet,
  },
];

const AUTO_SLIDE_INTERVAL_MS = 4000;

const WalletOnboardingB = () => {
  const [I18n] = useContext(LanguageContext);
  const [open, setOpen] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const navigate = useNavigate();
  const { accountInfo = {} } = useWalletContext();

  const isPending =
    accountInfo.emi_onboarding_status === EMIONBOARDINGSTATUS.SUBMITTED ||
    accountInfo.emi_onboarding_status === EMIONBOARDINGSTATUS.INPROGRESS;
  const isVerificationFailed =
    accountInfo.emi_onboarding_status === EMIONBOARDINGSTATUS.RESUBMIT;

  const slide = SLIDES[activeSlide];
  const isFirstSlide = activeSlide === 0;
  const isLastSlide = activeSlide === SLIDES.length - 1;

  // Autoplay advances until the last slide, then stops (no loop).
  useEffect(() => {
    if (isLastSlide) return undefined;
    const id = setTimeout(() => {
      setActiveSlide((current) =>
        current < SLIDES.length - 1 ? current + 1 : current,
      );
    }, AUTO_SLIDE_INTERVAL_MS);
    return () => clearTimeout(id);
  }, [activeSlide, isLastSlide]);

  const goToPrevSlide = () => {
    setActiveSlide((current) => (current > 0 ? current - 1 : current));
  };

  const goToNextSlide = () => {
    setActiveSlide((current) =>
      current < SLIDES.length - 1 ? current + 1 : current,
    );
  };

  const handleUpgradeAccount = () => {
    triggerLogEvent(analytics, "w_kyc");
    setOpen(true);
  };

  return (
    <MainContainer className="walletOnboardingB" fluid>
      <Grid container>
        <Grid item md={12}>
          <InnerWrapper className="innerWrapper newInnerWrapper">
            <Box className={styles.content}>
              <Box className={styles.backLinkBox}>
                <Box className={styles.logoBox}>
                  <LogoRupinNew />
                </Box>
                <ParagraphText
                  className={styles.backLink}
                  id="wallet_onboarding_b_back"
                  onClick={() => {
                    const lastVisited = localStorage.getItem("lastVisited");
                    if (lastVisited?.includes("UCash")) {
                      if (accountInfo.emi_onboarding_status !== EMIONBOARDINGSTATUS.VERIFIED) {
                        navigate("/BusinessOverview");
                      } else {
                        navigate("/UCash");
                      }
                    } else if (window.history.length > 1) {
                      navigate(-1);
                    } else {
                      navigate("/BusinessOverview");
                    }
                  }}
                >
                  <BackIconNew width={14} height={14} /> {I18n.back}
                </ParagraphText>
              </Box>
              <Box className={styles.contentInner}>
                <Box className={styles.carousel}>
                  <button
                    id="wallet_onboarding_b_prev_btn"
                    type="button"
                    className={`${styles.navBtn} ${styles.navBtnPrev}`}
                    aria-label={I18n.previous_slide}
                    disabled={isFirstSlide}
                    onClick={goToPrevSlide}
                  >
                    <SliderNextIcon width={24} height={24} />
                  </button>

                  <Box className={styles.phoneCard}>
                    <Box className={styles.slideHeader}>
                      <Box className={styles.progressBars}>
                        {SLIDES.map((_, index) => {
                          const isCompleted = index < activeSlide;
                          const isActive = index === activeSlide;
                          return (
                            <span
                              key={`${index}-${isActive ? activeSlide : "idle"}`}
                              className={`${styles.progressBar} ${
                                isCompleted ? styles.progressBarCompleted : ""
                              }`}
                            >
                              {isActive ? (
                                <span
                                  className={styles.progressBarFill}
                                  style={{
                                    animationDuration: `${AUTO_SLIDE_INTERVAL_MS}ms`,
                                  }}
                                />
                              ) : null}
                            </span>
                          );
                        })}
                      </Box>

                      <HeadingText className={styles.slideTitle}>
                        {I18n[slide.titleKey]}
                      </HeadingText>
                      <ParagraphText className={styles.slideSubtitle}>
                        {I18n[slide.subtitleKey]}
                      </ParagraphText>
                    </Box>

                    <Box
                      className={`${styles.slideVisual} ${slide.imageClass}`}
                    >
                      <img
                        key={activeSlide}
                        src={slide.image}
                        alt={I18n[slide.altKey]}
                        className={styles.slideImage}
                      />
                    </Box>

                    <UBButton
                      id="wallet_onboarding_b_upgrade_btn"
                      title={I18n.upgrade_account}
                      className={`textUppercase ${styles.upgradeBtn}`}
                      onClick={handleUpgradeAccount}
                    />
                  </Box>

                  <button
                    id="wallet_onboarding_b_next_btn"
                    type="button"
                    className={styles.navBtn}
                    aria-label={I18n.next_slide}
                    disabled={isLastSlide}
                    onClick={goToNextSlide}
                  >
                    <SliderNextIcon width={24} height={24} />
                  </button>
                </Box>
              </Box>
            </Box>
          </InnerWrapper>
        </Grid>
      </Grid>
      <WalletNonidealModal
        isPending={isPending}
        setOpen={setOpen}
        open={open}
        applicationID={accountInfo.application_id || null}
        raastDetail={accountInfo.raast_info || null}
        isVerificationFailed={isVerificationFailed}
      />
    </MainContainer>
  );
};

export default WalletOnboardingB;
